import React from 'react';
import { bool } from 'prop-types';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { propTypes } from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import { resolveLatestProcessName, getProcess } from '../../transactions/transaction';

import css from './OrderBreakdown.module.css';

const { Money } = sdkTypes;

const MEMBER_OFFSET_RE = /^line-item\/membership-host-offset-(regular|lastMinute)-(\d+)$/;

const numberFromMaybe = v => {
  if (v == null) return 0;
  if (typeof v === 'number') return v;
  if (typeof v.toNumber === 'function') return v.toNumber();
  return Number(v) || 0;
};

// For provider view on a member booking, the Sharetribe payoutTotal is $0
// (rental + offset net to zero in the transaction's own math). The host's
// actual payout comes via WetRentals direct deposit at the pool rate; we
// compute it from the offset line item and surface it as the "You'll make"
// amount so the host doesn't see a misleading $0.
const computeMemberProviderPayout = transaction => {
  const lineItems = transaction?.attributes?.lineItems || [];
  const offset = lineItems.find(li => MEMBER_OFFSET_RE.test(li.code) && !li.reversal);
  if (!offset) return null;
  // If the offset has a reversal in the line items (booking refunded /
  // cancelled / declined), fall through to the standard payout total —
  // which will be $0 — instead of showing the pool payout that won't pay.
  const reversed = lineItems.some(li => li.code === offset.code && li.reversal);
  if (reversed) return null;
  const match = offset.code.match(MEMBER_OFFSET_RE);
  const poolRateCents = parseInt(match[2], 10);
  const orderLine = lineItems.find(
    li => (li.code === 'line-item/day' || li.code === 'line-item/night') && !li.reversal
  );
  const days = numberFromMaybe(orderLine?.quantity || orderLine?.units);
  if (!days || !poolRateCents) return null;
  const currency = offset.unitPrice?.currency || 'CAD';
  return new Money(poolRateCents * days, currency);
};

const LineItemTotalPrice = props => {
  const { transaction, isProvider, intl } = props;
  const processName = resolveLatestProcessName(transaction?.attributes?.processName);
  if (!processName) {
    return null;
  }
  const process = getProcess(processName);
  const isCompleted = process.isCompleted(transaction?.attributes?.lastTransition);
  const isRefunded = process.isRefunded(transaction?.attributes?.lastTransition);

  let providerTotalMessageId = 'OrderBreakdown.providerTotalDefault';
  if (isCompleted) {
    providerTotalMessageId = 'OrderBreakdown.providerTotalReceived';
  } else if (isRefunded) {
    providerTotalMessageId = 'OrderBreakdown.providerTotalRefunded';
  }

  const totalLabel = isProvider ? (
    <FormattedMessage id={providerTotalMessageId} />
  ) : (
    <FormattedMessage id="OrderBreakdown.total" />
  );

  // Member-booking override: replace the $0 provider total with the actual
  // pool payout. Customer view, and non-member bookings, fall through to
  // the standard payin/payout math.
  const memberProviderPayout = isProvider ? computeMemberProviderPayout(transaction) : null;
  if (memberProviderPayout) {
    return (
      <>
        <hr className={css.totalDivider} />
        <div className={css.lineItemTotal}>
          <div className={css.totalLabel}>{totalLabel}</div>
          <div className={css.totalPrice}>{formatMoney(intl, memberProviderPayout)}</div>
        </div>
      </>
    );
  }

  const basePrice = isProvider
    ? transaction.attributes.payoutTotal
    : transaction.attributes.payinTotal;
  
  // Add supplementary charge if it exists
  const supplementaryCharge = transaction?.attributes?.metadata?.dispute?.dollarAmount || 0;
  // For customers, add the fee (23%) on top; for providers, just add the base amount
  const supplementaryTotal = isProvider 
    ? supplementaryCharge 
    : supplementaryCharge + Math.round(supplementaryCharge * 0.23);
  
  // Add delivery/pickup charges if they exist
  const deliveryAmount = transaction?.attributes?.metadata?.delivery?.deliveryAmount || 0;
  const pickupAmount = transaction?.attributes?.metadata?.pickup?.pickupAmount || 0;
  const deliveryTotal = deliveryAmount + pickupAmount;
  // For customers, add the fee (23%) on top; for providers, just add the base amount
  const deliveryWithFee = isProvider 
    ? deliveryTotal
    : deliveryTotal + Math.round(deliveryTotal * 0.23);
  
  const totalPriceAmount = basePrice.amount + supplementaryTotal + deliveryWithFee;
  const totalPrice = new Money(totalPriceAmount, basePrice.currency);
  
  const formattedTotalPrice = formatMoney(intl, totalPrice);

  return (
    <>
      <hr className={css.totalDivider} />
      <div className={css.lineItemTotal}>
        <div className={css.totalLabel}>{totalLabel}</div>
        <div className={css.totalPrice}>{formattedTotalPrice}</div>
      </div>
    </>
  );
};

LineItemTotalPrice.propTypes = {
  transaction: propTypes.transaction.isRequired,
  isProvider: bool.isRequired,
  intl: intlShape.isRequired,
};

export default LineItemTotalPrice;
